import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Home, Star, Send, Mail, MessageSquare, MessageCircle, 
  Users, RefreshCw, Check, X, Settings, Sparkles, History,
  Package, FileText, Receipt, Filter, Search, ExternalLink, Eye
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || window.location.origin;

export default function ReviewsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [selectedClients, setSelectedClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [showSettings, setShowSettings] = useState(false);
  const [settings, setSettings] = useState({
    shop_name: 'K&O PHONELAB',
    google_review_link: ''
  });
  
  // Modal d'envoi
  const [showSendModal, setShowSendModal] = useState(false);
  const [sendingClient, setSendingClient] = useState(null);
  const [channels, setChannels] = useState({ email: false, sms: false, whatsapp: false });
  const [generatedMessage, setGeneratedMessage] = useState('');
  const [generatingMessage, setGeneratingMessage] = useState(false);
  const [sending, setSending] = useState(false);
  const [useAI, setUseAI] = useState(true);

  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchClients();
    fetchSettings();
  }, []);

  useEffect(() => {
    filterClients();
  }, [clients, searchTerm, sourceFilter]);

  const fetchClients = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/reviews/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(response.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
      toast.error('Erreur lors du chargement des clients');
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API}/api/reviews/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistory(response.data);
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/api/reviews/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const saveSettings = async () => {
    try {
      await axios.post(`${API}/api/reviews/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Paramètres enregistrés');
      setShowSettings(false);
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const filterClients = () => {
    let filtered = clients;
    
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(c => 
        c.name?.toLowerCase().includes(search) ||
        c.phone?.includes(search) ||
        c.email?.toLowerCase().includes(search)
      );
    }
    
    if (sourceFilter !== 'all') {
      filtered = filtered.filter(c => c.source === sourceFilter);
    }
    
    setFilteredClients(filtered);
  };

  const openSendModal = (client) => {
    setSendingClient(client);
    setChannels({
      email: !!client.email,
      sms: !!client.phone,
      whatsapp: false
    });
    setGeneratedMessage('');
    setShowSendModal(true);
  };

  const generateMessage = async () => {
    if (!sendingClient) return;
    
    setGeneratingMessage(true);
    try {
      const response = await axios.post(`${API}/api/reviews/generate-message`, {
        client_name: sendingClient.name,
        purchase_type: sendingClient.purchase_type,
        visit_date: sendingClient.date
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGeneratedMessage(response.data.message);
    } catch (error) {
      toast.error('Erreur lors de la génération du message');
    } finally {
      setGeneratingMessage(false);
    }
  };

  const sendReviewRequest = async () => {
    if (!sendingClient) return;
    
    const selectedChannels = [];
    if (channels.email) selectedChannels.push('email');
    if (channels.sms) selectedChannels.push('sms');
    if (channels.whatsapp) selectedChannels.push('whatsapp');
    
    if (selectedChannels.length === 0) {
      toast.error('Sélectionnez au moins un canal');
      return;
    }
    
    setSending(true);
    try {
      const response = await axios.post(`${API}/api/reviews/send`, {
        client_name: sendingClient.name,
        client_phone: sendingClient.phone,
        client_email: sendingClient.email,
        source: sendingClient.source,
        source_id: sendingClient.source_id,
        purchase_type: sendingClient.purchase_type,
        visit_date: sendingClient.date,
        channels: selectedChannels,
        custom_message: generatedMessage || null,
        use_ai: useAI
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success('Demande d\'avis envoyée !');
        setShowSendModal(false);
        // Retirer le client de la liste
        setClients(prev => prev.filter(c => c.id !== sendingClient.id));
      }
    } catch (error) {
      toast.error('Erreur lors de l\'envoi');
    } finally {
      setSending(false);
    }
  };

  const getSourceIcon = (source) => {
    switch (source) {
      case 'order': return <Package className="w-4 h-4 text-amber-400" />;
      case 'invoice': return <FileText className="w-4 h-4 text-blue-400" />;
      case 'deposit': return <Receipt className="w-4 h-4 text-purple-400" />;
      default: return <Users className="w-4 h-4 text-gray-400" />;
    }
  };

  const getSourceLabel = (source) => {
    switch (source) {
      case 'order': return 'Commande';
      case 'invoice': return 'Facture';
      case 'deposit': return 'Bon d\'entrée';
      default: return source;
    }
  };

  // Fonction pour voir le document d'origine
  const viewSourceDocument = (client) => {
    // Naviguer directement vers la page avec le bon paramètre
    switch (client.source) {
      case 'order':
        navigate(`/order-alerts?search=${encodeURIComponent(client.name)}`);
        break;
      case 'invoice':
        navigate(`/invoices?search=${encodeURIComponent(client.name)}`);
        break;
      case 'deposit':
        navigate(`/deposit-receipts?search=${encodeURIComponent(client.ticket_number || client.name)}`);
        break;
      default:
        toast.info('Document source non identifié');
    }
  };

  // Formater la date d'envoi
  const formatSentDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-slate-900 to-gray-900">
      {/* Header */}
      <header className="bg-gray-800/50 backdrop-blur-sm border-b border-gray-700 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/')}
              variant="ghost"
              size="sm"
              className="text-white/70 hover:text-white"
            >
              <Home className="w-4 h-4 mr-2" />
              Accueil
            </Button>
            <div className="flex items-center gap-2">
              <Star className="w-6 h-6 text-yellow-400" />
              <h1 className="text-xl font-bold text-white">Demandes d'Avis</h1>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              onClick={() => { fetchHistory(); setShowHistory(true); }}
              variant="ghost"
              size="sm"
              className="text-white/70 hover:text-white"
            >
              <History className="w-4 h-4 mr-2" />
              Historique
            </Button>
            <Button
              onClick={() => setShowSettings(true)}
              variant="ghost"
              size="sm"
              className="text-white/70 hover:text-white"
            >
              <Settings className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Filtres */}
        <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-4 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Rechercher un client..."
                  className="pl-10 bg-white/10 border-white/20 text-white"
                />
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <select
                value={sourceFilter}
                onChange={(e) => setSourceFilter(e.target.value)}
                className="bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm"
              >
                <option value="all">Toutes les sources</option>
                <option value="client">Clients</option>
                <option value="order">Commandes</option>
                <option value="invoice">Factures</option>
                <option value="deposit">Bons d'entrée</option>
              </select>
            </div>
            
            <Button
              onClick={fetchClients}
              variant="outline"
              size="sm"
              className="border-white/20 text-white hover:bg-white/10"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Actualiser
            </Button>
          </div>
          
          <div className="mt-3 text-sm text-gray-400">
            {filteredClients.length} client{filteredClients.length > 1 ? 's' : ''} trouvé{filteredClients.length > 1 ? 's' : ''}
          </div>
        </div>

        {/* Liste des clients */}
        {loading ? (
          <div className="flex justify-center py-12">
            <RefreshCw className="w-8 h-8 text-yellow-400 animate-spin" />
          </div>
        ) : filteredClients.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <Star className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>Aucun client à contacter pour le moment</p>
            <p className="text-sm mt-2">Les clients apparaîtront ici après une commande, facture ou bon d'entrée</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {filteredClients.map((client) => (
              <div
                key={`${client.source}-${client.id}`}
                className="bg-gray-800/50 rounded-xl border border-gray-700 p-4 hover:border-yellow-500/50 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-yellow-500/20 flex items-center justify-center">
                      <span className="text-yellow-400 font-bold text-lg">
                        {client.name?.charAt(0).toUpperCase() || '?'}
                      </span>
                    </div>
                    
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-white">{client.name}</h3>
                        <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-gray-700 text-gray-300">
                          {getSourceIcon(client.source)}
                          {getSourceLabel(client.source)}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-sm text-gray-400">
                        {client.phone && <span>{client.phone}</span>}
                        {client.email && <span>{client.email}</span>}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {client.purchase_type} • {client.date}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {/* Badge "Déjà envoyé" */}
                    {client.request_sent && (
                      <span className="flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                        <Check className="w-3 h-3" />
                        Envoyé {client.sent_at ? formatSentDate(client.sent_at) : ''}
                      </span>
                    )}
                    
                    {/* Bouton voir le document d'origine */}
                    <Button
                      onClick={() => viewSourceDocument(client)}
                      variant="ghost"
                      size="sm"
                      className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/10"
                      title={`Voir ${getSourceLabel(client.source)}`}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      Voir
                    </Button>
                    
                    <Button
                      onClick={() => openSendModal(client)}
                      className={client.request_sent 
                        ? "bg-gray-600 hover:bg-gray-500 text-white" 
                        : "bg-yellow-500 hover:bg-yellow-600 text-black"
                      }
                    >
                      {client.request_sent ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Renvoyer
                        </>
                      ) : (
                        <>
                          <Star className="w-4 h-4 mr-2" />
                          Demander un avis
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Modal d'envoi */}
      {showSendModal && sendingClient && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl border border-gray-700 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <Star className="w-5 h-5 text-yellow-400" />
                  Demander un avis
                </h2>
                <Button
                  onClick={() => setShowSendModal(false)}
                  variant="ghost"
                  size="sm"
                  className="text-gray-400 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>

              {/* Info client */}
              <div className="bg-gray-700/50 rounded-lg p-4 mb-6">
                <div className="font-semibold text-white">{sendingClient.name}</div>
                <div className="text-sm text-gray-400 mt-1">
                  {sendingClient.purchase_type} • {sendingClient.date}
                </div>
                <div className="text-sm text-gray-400">
                  {sendingClient.phone && <span className="mr-3">{sendingClient.phone}</span>}
                  {sendingClient.email && <span>{sendingClient.email}</span>}
                </div>
              </div>

              {/* Canaux */}
              <div className="space-y-3 mb-6">
                <label className="text-sm font-medium text-white">Canaux d'envoi</label>
                
                {sendingClient.email && (
                  <label className="flex items-center gap-3 p-3 rounded-lg border border-white/10 cursor-pointer hover:bg-white/5">
                    <input
                      type="checkbox"
                      checked={channels.email}
                      onChange={(e) => setChannels({...channels, email: e.target.checked})}
                      className="w-4 h-4 rounded"
                    />
                    <Mail className="w-5 h-5 text-blue-400" />
                    <span className="text-white">Email</span>
                  </label>
                )}
                
                {sendingClient.phone && (
                  <>
                    <label className="flex items-center gap-3 p-3 rounded-lg border border-white/10 cursor-pointer hover:bg-white/5">
                      <input
                        type="checkbox"
                        checked={channels.sms}
                        onChange={(e) => setChannels({...channels, sms: e.target.checked})}
                        className="w-4 h-4 rounded"
                      />
                      <MessageSquare className="w-5 h-5 text-green-400" />
                      <span className="text-white">SMS</span>
                    </label>
                    
                    <label className="flex items-center gap-3 p-3 rounded-lg border border-green-500/30 cursor-pointer hover:bg-green-500/10 bg-green-500/5">
                      <input
                        type="checkbox"
                        checked={channels.whatsapp}
                        onChange={(e) => setChannels({...channels, whatsapp: e.target.checked})}
                        className="w-4 h-4 rounded accent-green-500"
                      />
                      <MessageCircle className="w-5 h-5 text-green-500" />
                      <span className="text-green-400">WhatsApp</span>
                    </label>
                  </>
                )}
              </div>

              {/* Génération IA */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-medium text-white flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-purple-400" />
                    Message personnalisé par IA
                  </label>
                  <Button
                    onClick={generateMessage}
                    disabled={generatingMessage}
                    size="sm"
                    className="bg-purple-500 hover:bg-purple-600"
                  >
                    {generatingMessage ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-1" />
                        Générer
                      </>
                    )}
                  </Button>
                </div>
                
                <textarea
                  value={generatedMessage}
                  onChange={(e) => setGeneratedMessage(e.target.value)}
                  placeholder="Cliquez sur 'Générer' pour créer un message personnalisé ou écrivez le vôtre..."
                  className="w-full h-40 bg-white/10 border border-white/20 rounded-lg p-3 text-white text-sm resize-none"
                />
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <Button
                  onClick={() => setShowSendModal(false)}
                  variant="outline"
                  className="flex-1 border-white/20 text-white hover:bg-white/10"
                >
                  Annuler
                </Button>
                <Button
                  onClick={sendReviewRequest}
                  disabled={sending || (!channels.email && !channels.sms && !channels.whatsapp)}
                  className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-black"
                >
                  {sending ? (
                    <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Envoyer
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Historique */}
      {showHistory && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl border border-gray-700 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <History className="w-5 h-5 text-blue-400" />
                  Historique des demandes
                </h2>
                <Button
                  onClick={() => setShowHistory(false)}
                  variant="ghost"
                  size="sm"
                  className="text-gray-400 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>

              {history.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  Aucune demande envoyée pour le moment
                </div>
              ) : (
                <div className="space-y-3">
                  {history.map((item) => (
                    <div key={item.id} className="bg-gray-700/50 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="font-semibold text-white">{item.client_name}</div>
                        <div className="text-xs text-gray-400">
                          {new Date(item.created_at).toLocaleDateString('fr-FR')}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 mt-2">
                        {item.results?.email?.success && (
                          <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-1 rounded">Email ✓</span>
                        )}
                        {item.results?.sms?.success && (
                          <span className="text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded">SMS ✓</span>
                        )}
                        {item.results?.whatsapp?.success && (
                          <span className="text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded">WhatsApp ✓</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal Paramètres */}
      {showSettings && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl border border-gray-700 max-w-lg w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <Settings className="w-5 h-5 text-gray-400" />
                  Paramètres des avis
                </h2>
                <Button
                  onClick={() => setShowSettings(false)}
                  variant="ghost"
                  size="sm"
                  className="text-gray-400 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-white/70 mb-2">Nom de la boutique</label>
                  <Input
                    value={settings.shop_name}
                    onChange={(e) => setSettings({...settings, shop_name: e.target.value})}
                    className="bg-white/10 border-white/20 text-white"
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-white/70 mb-2 flex items-center gap-2">
                    <ExternalLink className="w-4 h-4" />
                    Lien Google Avis
                  </label>
                  <Input
                    value={settings.google_review_link}
                    onChange={(e) => setSettings({...settings, google_review_link: e.target.value})}
                    placeholder="https://g.page/r/..."
                    className="bg-white/10 border-white/20 text-white"
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <Button
                  onClick={() => setShowSettings(false)}
                  variant="outline"
                  className="flex-1 border-white/20 text-white hover:bg-white/10"
                >
                  Annuler
                </Button>
                <Button
                  onClick={saveSettings}
                  className="flex-1 bg-blue-500 hover:bg-blue-600"
                >
                  Enregistrer
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
